/**
 * Finds matching override configuration based on query parameters
 *
 * @param {Array} overrides - Array of override configurations
 * @param {Object} queryParams - Query parameters object from URL
 * @returns {Array|Object|null} - Returns override.ids for landing overrides, or override object for other types, null otherwise
 *
 * @example
 * // OR logic - matches if ANY parameter satisfies conditions
 * const overrides = [
 *   {
 *     matcher: { utm_source: 'facebook', utm_term: 'test' },
 *     ids: ['landing-1', 'landing-2']
 *   }
 * ]
 *
 * // AND logic (object) - matches if ALL parameters satisfy conditions
 * const overrides = [
 *   {
 *     matcher: { $and: { utm_source: 'facebook', utm_term: 'test' } },
 *     ids: ['landing-1', 'landing-2']
 *   }
 * ]
 *
 * // OR logic (explicit) - matches if ANY parameter satisfies conditions
 * const overrides = [
 *   {
 *     matcher: { $or: { utm_source: 'facebook', utm_term: 'test' } },
 *     ids: ['landing-1', 'landing-2']
 *   }
 * ]
 *
 * // Complex logic - combines AND and OR
 * const overrides = [
 *   {
 *     matcher: {
 *       $and: {
 *         country: 'us',
 *         $or: {
 *           utm_content: /excel/,
 *           utm_term: /excel/
 *         }
 *       }
 *     },
 *     ids: ['landing-1', 'landing-2']
 *   }
 * ]
 */
export function findMatchingOverride(overrides, queryParams) {
	const override = matchOverride(overrides, queryParams)

	if (!override) {
		return null
	}

	// For backwards compatibility with landing overrides, return ids if present
	// For new paywall overrides, return the full override object
	return override.ids || override
}

/**
 * Same matching, but always the WHOLE override object — use it when the caller needs a
 * field that `findMatchingOverride` would swallow by returning `ids` (a landing override
 * also carries the medication its creative advertises, DEV-35776).
 *
 * @param {Array} overrides - Array of override configurations
 * @param {Object} queryParams - Query parameters object
 * @returns {Object|null} - The matching override, or null
 */
export function matchOverride(overrides, queryParams) {
	if (!Array.isArray(overrides)) {
		return null
	}

	for (const override of overrides) {
		if (matchesOverride(override, queryParams)) {
			return override
		}
	}

	return null
}

/**
 * Checks if override configuration matches given query parameters
 *
 * @param {Object} override - Override configuration object
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if override matches, false otherwise
 */
function matchesOverride(override, queryParams) {
	if (!override.matcher) {
		return false
	}

	return evaluateMatchers(override.matcher, queryParams)
}

/**
 * Evaluates matcher conditions recursively
 *
 * @param {Object} matchers - Matcher configuration object
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if matchers match, false otherwise
 */
function evaluateMatchers(matchers, queryParams) {
	// Check for explicit $and logic
	if (matchers.$and) {
		return evaluateAndMatchers(matchers.$and, queryParams)
	}

	// Check for explicit $or logic
	if (matchers.$or) {
		return evaluateOrMatchers(matchers.$or, queryParams)
	}

	// Default behavior: if no explicit logic operators, use OR logic for compatibility
	return checkAnyMatcher(matchers, queryParams)
}

/**
 * Evaluates AND logic matchers
 *
 * @param {Object} matchers - Matcher configuration object
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if all matchers match, false otherwise
 */
function evaluateAndMatchers(matchers, queryParams) {
	for (const key in matchers) {
		const matcherValue = matchers[key]

		// Handle nested $or within $and
		if (key === '$or') {
			if (!evaluateOrMatchers(matcherValue, queryParams)) {
				return false
			}
		}
		// Handle nested $and within $and
		else if (key === '$and') {
			if (!evaluateAndMatchers(matcherValue, queryParams)) {
				return false
			}
		}
		// Handle regular matcher
		else if (!checkSingleMatcher(key, matcherValue, queryParams)) {
			return false
		}
	}
	return true
}

/**
 * Evaluates OR logic matchers
 *
 * @param {Object} matchers - Matcher configuration object
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if any matcher matches, false otherwise
 */
function evaluateOrMatchers(matchers, queryParams) {
	for (const key in matchers) {
		const matcherValue = matchers[key]

		// Handle nested $and within $or
		if (key === '$and') {
			if (evaluateAndMatchers(matcherValue, queryParams)) {
				return true
			}
		}
		// Handle nested $or within $or
		else if (key === '$or') {
			if (evaluateOrMatchers(matcherValue, queryParams)) {
				return true
			}
		}
		// Handle regular matcher
		else if (checkSingleMatcher(key, matcherValue, queryParams)) {
			return true
		}
	}
	return false
}

/**
 * Checks if ANY matcher satisfies conditions (OR logic)
 *
 * @param {Object} matchers - Matcher configuration object
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if any matcher matches, false otherwise
 */
function checkAnyMatcher(matchers, queryParams) {
	for (const key in matchers) {
		if (checkSingleMatcher(key, matchers[key], queryParams)) {
			return true
		}
	}
	return false
}

/**
 * Checks if single matcher satisfies condition
 *
 * @param {string} key - Parameter name (e.g., 'utm_source')
 * @param {string|RegExp} matcherValue - Expected value or regex pattern
 * @param {Object} queryParams - Query parameters object
 * @returns {boolean} - True if matcher matches, false otherwise
 */
function checkSingleMatcher(key, matcherValue, queryParams) {
	let queryParamValue = queryParams[key]

	if (typeof queryParamValue !== 'string') {
		return false
	}

	queryParamValue = queryParamValue.toLowerCase()

	// Support both regex and string matching
	return matcherValue?.test?.(queryParamValue) ?? queryParamValue === matcherValue
}
